import { useState, memo, useCallback, useMemo, useEffect, useRef, useLayoutEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { LoadFinderTableHeader } from "./LoadFinderTableHeader";
import { TableHead } from "@/components/common/table/table";
import { SortableTableHead } from "@/components/dataPages/loadboard";
import { ErrorComponent } from "@/components/common/errors";
import dayjs from "dayjs";
import { TruckFilterLoadRow } from "./TruckFilterLoadRow";

const ROW_HEIGHT = 57; // Fixed row height for virtualization (matches h-[57px] in TruckFilterLoadRow)
const TABLE_HEADER_HEIGHT = 44; // Height of the sticky <thead> (h-11)
const SCROLL_TARGET_GAP = 20; // Visual gap between sticky header bottom and the new load row

// Virtualized table body for performance with large datasets
const VirtualizedTableBody = memo(
    ({
        sortedLoads,
        hideTruck,
        hideLoadId,
        getCmScore,
        getOfferDiffPercentage,
        newLoadTimestamps,
        getLoad,
        currentSort,
        onSort,
        scrollToNewLoadTrigger,
        externalScrollContainerRef,
        tableHeaderStickyOffset = 0,
    }) => {
        const tbodyRef = useRef(null);
        const [measuredScrollMargin, setMeasuredScrollMargin] = useState(0);
        const lastProcessedTriggerRef = useRef(0);

        // Measure the distance from scroll container origin to tbody start
        useLayoutEffect(() => {
            const measureOffset = () => {
                const scrollContainer = externalScrollContainerRef?.current;
                const tbody = tbodyRef.current;
                if (!scrollContainer || !tbody) return;

                const scrollTop = scrollContainer.scrollTop;
                const tbodyRect = tbody.getBoundingClientRect();
                const containerRect = scrollContainer.getBoundingClientRect();
                // Calculate absolute offset from scroll origin to tbody
                const offset = Math.max(0, Math.round(scrollTop + (tbodyRect.top - containerRect.top)));
                setMeasuredScrollMargin(offset);
            };

            // Small delay to ensure layout is stable after filters collapse/expand
            const timeoutId = setTimeout(measureOffset, 50);

            // Also observe for resize changes
            const resizeObserver = new ResizeObserver(measureOffset);
            if (externalScrollContainerRef?.current) {
                resizeObserver.observe(externalScrollContainerRef.current);
            }
            return () => {
                clearTimeout(timeoutId);
                resizeObserver.disconnect();
            };
        }, [externalScrollContainerRef, tableHeaderStickyOffset]);

        const virtualizer = useVirtualizer({
            count: sortedLoads.length,
            getScrollElement: () => externalScrollContainerRef?.current,
            estimateSize: () => ROW_HEIGHT,
            overscan: 5,
            // Account for content above the table (filters section, loads header, table header)
            scrollMargin: measuredScrollMargin,
        });

        // Scroll to first new load when trigger changes
        useEffect(() => {
            // Only scroll if the trigger actually changed (not just other dependencies)
            if (scrollToNewLoadTrigger === 0 || scrollToNewLoadTrigger === lastProcessedTriggerRef.current || sortedLoads.length === 0)
                return;

            // Mark this trigger as processed immediately to prevent re-triggers
            lastProcessedTriggerRef.current = scrollToNewLoadTrigger;

            // Find the index of the first new load in the sorted array
            const firstNewIndex = sortedLoads.findIndex((item) => {
                const loadId = getLoad(item).uniqueId;
                return loadId && newLoadTimestamps[loadId] != null;
            });

            if (firstNewIndex >= 0) {
                // Use multiple frames to ensure layout is stable and virtualizer is ready
                const scrollToLoad = () => {
                    const scrollContainer = externalScrollContainerRef?.current;
                    if (!scrollContainer) return;

                    // Place the row just below all sticky elements (LoadFinder header + companion + sticky thead)
                    // with a small visual gap. tableHeaderStickyOffset is the top edge of the sticky thead;
                    // add the thead's own height so the row lands below it, not behind it.
                    const targetScrollTop =
                        measuredScrollMargin +
                        firstNewIndex * ROW_HEIGHT -
                        tableHeaderStickyOffset -
                        TABLE_HEADER_HEIGHT -
                        SCROLL_TARGET_GAP;

                    scrollContainer.scrollTo({
                        top: Math.max(0, targetScrollTop),
                        behavior: "smooth",
                    });
                };

                // Double RAF to ensure layout is complete
                requestAnimationFrame(() => {
                    requestAnimationFrame(scrollToLoad);
                });
            }
        }, [
            scrollToNewLoadTrigger,
            sortedLoads,
            newLoadTimestamps,
            getLoad,
            measuredScrollMargin,
            tableHeaderStickyOffset,
            externalScrollContainerRef,
        ]);

        const virtualItems = virtualizer.getVirtualItems();
        const totalHeight = virtualizer.getTotalSize();

        // Calculate spacer heights - subtract scrollMargin since that offset is for content above the table, not empty space within it
        const topSpacerHeight = virtualItems.length > 0 ? Math.max(0, virtualItems[0].start - measuredScrollMargin) : 0;
        const bottomSpacerHeight = virtualItems.length > 0 ? totalHeight - virtualItems[virtualItems.length - 1].end : 0;

        // Fallback: if virtualizer hasn't initialized yet, show first batch of items
        const itemsToRender =
            virtualItems.length > 0
                ? virtualItems
                : sortedLoads.slice(0, 20).map((_, i) => ({ index: i, start: i * ROW_HEIGHT, end: (i + 1) * ROW_HEIGHT }));

        return (
            <div className="w-full relative overflow-x-clip">
                <table className="w-full border-spacing-0 caption-bottom text-sm" style={{ tableLayout: "fixed" }}>
{/* Column widths: CM Score, Pickup, Delivery, Pickup date, Delivery date, Distance, Broker, Offer, CM estimate, Age, Actions */}
                    <colgroup>
                        <col style={{ width: "8%", minWidth: "90px" }} />
                        <col style={{ width: "9%", minWidth: "90px" }} />
                        <col style={{ width: "9%", minWidth: "90px" }} />
                        <col style={{ width: "9%", minWidth: "110px" }} />
                        <col style={{ width: "9%", minWidth: "110px" }} />
                        <col style={{ width: "9%", minWidth: "90px" }} />
                        <col style={{ width: "18%" }} />
                        <col style={{ width: "7%", minWidth: "75px" }} />
                        <col style={{ width: "12%", minWidth: "115px" }} />
                        <col style={{ width: "5%", minWidth: "55px" }} />
                        <col style={{ width: "5%", minWidth: "45px" }} />
                    </colgroup>
                    <thead
                        className="bg-cm-p-background h-11 sticky z-10"
                        style={{ top: `${tableHeaderStickyOffset}px`, boxShadow: "0 1px 0 0 var(--cm-p-outline)" }}
                    >
                        <tr className="h-11 uppercase text-xs [&_th]:text-xs [&_th]:font-medium [&_th]:text-cm-p-text-primary">
                            <SortableTableHead className="pl-2" sortKey="cmScore" currentSort={currentSort} onSort={onSort} arrowIcon>
                                CM Score
                            </SortableTableHead>
                            {!hideTruck && (
                                <SortableTableHead sortKey="truckReference" currentSort={currentSort} onSort={onSort} arrowIcon>
                                    Truck
                                </SortableTableHead>
                            )}
                            {!hideLoadId && <TableHead>LoadID</TableHead>}
                            <SortableTableHead sortKey="pickup" currentSort={currentSort} onSort={onSort} arrowIcon>
                                Pickup
                            </SortableTableHead>
                            <SortableTableHead sortKey="delivery" currentSort={currentSort} onSort={onSort} arrowIcon>
                                Delivery
                            </SortableTableHead>
                            <SortableTableHead sortKey="pickupDate" currentSort={currentSort} onSort={onSort} arrowIcon>
                                Pickup date
                            </SortableTableHead>
                            <SortableTableHead sortKey="deliveryDate" currentSort={currentSort} onSort={onSort} arrowIcon>
                                Delivery date
                            </SortableTableHead>
                            <SortableTableHead sortKey="distance" currentSort={currentSort} onSort={onSort} arrowIcon alignRight>
                                Distance
                            </SortableTableHead>
                            <SortableTableHead sortKey="broker" currentSort={currentSort} onSort={onSort} arrowIcon>
                                Broker
                            </SortableTableHead>
                            <SortableTableHead sortKey="offer" currentSort={currentSort} onSort={onSort} arrowIcon alignRight>
                                Offer
                            </SortableTableHead>
                            <SortableTableHead sortKey="cmEstimate" currentSort={currentSort} onSort={onSort} arrowIcon alignRight>
                                CM estimate
                            </SortableTableHead>
                            <SortableTableHead sortKey="age" currentSort={currentSort} onSort={onSort} arrowIcon alignRight>
                                Age
                            </SortableTableHead>
                            <TableHead />
                        </tr>
                    </thead>
                    <tbody ref={tbodyRef} className="border-0">
                        {/* Top padding row */}
                        {topSpacerHeight > 0 && (
                            <tr>
                                <td colSpan={100} style={{ height: topSpacerHeight, padding: 0, border: 0 }} />
                            </tr>
                        )}
                        {itemsToRender.map((virtualRow) => {
                            const item = sortedLoads[virtualRow.index];
                            const loadId = getLoad(item).uniqueId;
                            const isNew = loadId && newLoadTimestamps[loadId] != null;
                            const isLast = virtualRow.index === sortedLoads.length - 1;
                            return (
                                <TruckFilterLoadRow
                                    key={loadId || virtualRow.index}
                                    item={item}
                                    index={virtualRow.index}
                                    withTruck={!hideTruck}
                                    hideLoadId={hideLoadId}
                                    cmScore={getCmScore(item)}
                                    offerDiffPercentage={getOfferDiffPercentage(item)}
                                    isNew={isNew}
                                    isLast={isLast}
                                />
                            );
                        })}
                        {/* Bottom padding row */}
                        {bottomSpacerHeight > 0 && (
                            <tr>
                                <td colSpan={100} style={{ height: bottomSpacerHeight, padding: 0, border: 0 }} />
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    },
);

export const TruckFilterLoads = ({
    children,
    loads,
    loading,
    searching = false, // strategies/loads resolving for a newly selected truck
    error,
    hideLoadId = false,
    hideTruck = false,
    headerVariant,
    headerName,
    averageEarnMoreOn = 2,
    defaultSortKey = "age",
    defaultSortDirection = "desc",
    newLoadTimestamps = {},
    scrollToNewLoadTrigger = 0,
    stickyHeaderOffset = null, // Offset from top for sticky header (null = not sticky)
    scrollContainerRef = null, // External scroll container ref for virtualization
    tableToggleRef = null, // Ref for the table toggle element (for scroll detection)
}) => {
    const [sortKey, setSortKey] = useState(defaultSortKey);
    const [sortDirection, setSortDirection] = useState(defaultSortDirection);
    const [isTableExpanded, setIsTableExpanded] = useState(true);

    const handleSort = useCallback((key, direction) => {
        setSortKey(key);
        setSortDirection(direction);
    }, []);

    const currentSort = useMemo(() => ({ key: sortKey, direction: sortDirection }), [sortKey, sortDirection]);

    // Helper to get load data from enriched item
    const getLoad = (item) => item?.loadBoardItem ?? item;
    const getRouteAnalysis = (item) => item?.routeAnalysis;

    // Calculate CM score from cmiReal array based on earnMoreOn index
    const getCmScore = (item) => {
        const routeAnalysis = getRouteAnalysis(item);
        if (!routeAnalysis?.cmiReal?.length) return null;
        // earnMoreOn specifies the index into cmiReal array, default to 2
        const earnMoreOnIndex = averageEarnMoreOn != null ? Math.round(averageEarnMoreOn) : 2;
        const index = Math.min(Math.max(0, earnMoreOnIndex), routeAnalysis.cmiReal.length - 1);
        const score = routeAnalysis.cmiReal[index];
        return score != null ? Math.round(score) : null;
    };

    // CM rate estimates only apply to single pickup/delivery loads, not multistop
    const getCmEstimate = (item) => {
        const load = getLoad(item);
        if ((load?.itineraryStops?.length ?? 0) > 2) return null;
        return getRouteAnalysis(item)?.estimatedPrice ?? null;
    };

    const getOfferDiffPercentage = (item) => {
        const load = getLoad(item);
        const estimatedPrice = getCmEstimate(item);
        if (!estimatedPrice || !load?.offerRate) return null;
        return ((load.offerRate - estimatedPrice) / estimatedPrice) * 100;
    };

    const sortedLoads = loads
        ? [...loads].sort((a, b) => {
              const loadA = getLoad(a);
              const loadB = getLoad(b);
              let aValue, bValue;
              switch (sortKey) {
                  case "age":
                      aValue = loadA.updatedAt || loadA.createdAt ? dayjs(loadA.updatedAt || loadA.createdAt).unix() : 0;
                      bValue = loadB.updatedAt || loadB.createdAt ? dayjs(loadB.updatedAt || loadB.createdAt).unix() : 0;
                      break;
                  case "truckReference":
                      aValue = loadA.truckReference?.name || "";
                      bValue = loadB.truckReference?.name || "";
                      break;
                  case "pickup":
                      aValue = loadA.itineraryStops?.[0]?.stopCity || "";
                      bValue = loadB.itineraryStops?.[0]?.stopCity || "";
                      break;
                  case "delivery":
                      aValue = loadA.itineraryStops?.[loadA.itineraryStops?.length - 1]?.stopCity || "";
                      bValue = loadB.itineraryStops?.[loadB.itineraryStops?.length - 1]?.stopCity || "";
                      break;
                  case "pickupDate":
                      aValue = loadA.pickupDate ? dayjs(loadA.pickupDate).unix() : 0;
                      bValue = loadB.pickupDate ? dayjs(loadB.pickupDate).unix() : 0;
                      break;
                  case "deliveryDate":
                      aValue = loadA.deliveryDate ? dayjs(loadA.deliveryDate).unix() : 0;
                      bValue = loadB.deliveryDate ? dayjs(loadB.deliveryDate).unix() : 0;
                      break;
                  case "distance":
                      aValue = loadA.loadDistance || 0;
                      bValue = loadB.loadDistance || 0;
                      break;
                  case "broker":
                      aValue = loadA.brokerInfo?.name || "";
                      bValue = loadB.brokerInfo?.name || "";
                      break;
                  case "offer":
                      aValue = loadA.offerRate || 0;
                      bValue = loadB.offerRate || 0;
                      break;
                  case "cmEstimate":
                      aValue = getCmEstimate(a) || 0;
                      bValue = getCmEstimate(b) || 0;
                      break;
                  case "cmScore":
                      aValue = getCmScore(a) ?? 0;
                      bValue = getCmScore(b) ?? 0;
                      break;
                  default:
                      return 0;
              }
              if (typeof aValue === "string" && typeof bValue === "string") {
                  return sortDirection === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
              }
              return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
          })
        : [];

    return (
        <div className="flex flex-col w-full relative">
            {headerVariant && (
                <div
                    ref={tableToggleRef}
                    className={stickyHeaderOffset != null ? "sticky z-20 bg-cm-p-surface" : "bg-cm-p-surface"}
                    style={stickyHeaderOffset != null ? { top: `${stickyHeaderOffset}px` } : undefined}
                >
                    <LoadFinderTableHeader
                        count={loads === null ? null : loads.length}
                        name={headerName}
                        variant={headerVariant}
                        isExpanded={isTableExpanded}
                        onToggle={sortedLoads.length > 0 ? () => setIsTableExpanded((v) => !v) : undefined}
                        loading={loading}
                        searching={searching}
                    />
                </div>
            )}
            {children}

            {error ? (
                <div className="flex-1 min-h-[300px]">
                    <ErrorComponent error={error} />
                </div>
            ) : !searching && sortedLoads.length > 0 ? (
                <div className="flex flex-col" style={{ display: isTableExpanded ? "flex" : "none" }}>
                    <VirtualizedTableBody
                        sortedLoads={sortedLoads}
                        hideTruck={hideTruck}
                        hideLoadId={hideLoadId}
                        getCmScore={getCmScore}
                        getOfferDiffPercentage={getOfferDiffPercentage}
                        newLoadTimestamps={newLoadTimestamps}
                        getLoad={getLoad}
                        currentSort={currentSort}
                        onSort={handleSort}
                        scrollToNewLoadTrigger={scrollToNewLoadTrigger}
                        externalScrollContainerRef={scrollContainerRef}
                        tableHeaderStickyOffset={stickyHeaderOffset != null ? stickyHeaderOffset + 63 : 0}
                    />
                </div>
            ) : null}
        </div>
    );
};
